import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import CustomButton from "../../../pages/CustomButton";
import Loader from "../../../dist/Loader";

// Format date for input + API
const toISODate = (d) => {
  const dd = new Date(d);
  return `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(dd.getDate()).padStart(2, "0")}`;
};

// FIXED Date generator (No repeat / no shifting)
const eachDate = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  const out = [];

  if (isNaN(s) || isNaN(e) || s > e) return out;

  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push({
      dateObj: new Date(d),
      date: String(d.getDate()).padStart(2, "0"),
      dayShort: ["S", "M", "T", "W", "T", "F", "S"][d.getDay()],
      apiKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`,
    });
  }

  return out;
};

// Mapping API data
const mapRow = (raw) => ({
  product_id: raw.product_id ?? "-",
  product_name: raw.product_name ?? "-",
  packing: raw.packing ?? "-",
  rate: raw.rate ?? 0,
  total_qty: raw.total_qty ?? 0,
  total_pob: raw.total_pob ?? 0,
  days: raw.days || {},
});

const StateWiseProduct = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [chemistList, setChemistList] = useState([{ id: "All", name: "All" }]);

  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedChemist, setSelectedChemist] = useState("All");

  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All" },
  ]);
  const [selectedEmployee, setSelectedEmployee] = useState("All");

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(toISODate(firstOfMonth));
  const [endDate, setEndDate] = useState(toISODate(today));

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dateList = useMemo(
    () => eachDate(startDate, endDate),
    [startDate, endDate],
  );

  /** Fetch States */
  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then((res) => setStates([{ id: "All", name: "All" }, ...res.data]));
  }, []);

  /** Fetch Division */
  useEffect(() => {
    if (!selectedState) return;
    axios
      .get(`${BASE_URL}/api/divisions?state_id=${selectedState}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setDivisions([{ id: "All", name: "All" }, ...res.data]));
  }, [selectedState]);

  /** Fetch Employees */
  const fetchEmployees = useCallback(() => {
    if (!selectedState || selectedState === "All") {
      setEmployees([{ id: "All", first_name: "All" }]);
      return;
    }

    const params = new URLSearchParams();
    params.set("state_id", selectedState);

    // ✅ ADD division filter
    if (selectedDivision !== "All") {
      const div = divisions.find((d) => d.id == selectedDivision);
      if (div) params.set("division", div.name);
    }

    axios
      .get(`${BASE_URL}/api/employees?${params.toString()}`, {
        headers: getAuthHeaders(),
      })
      .then((res) =>
        setEmployees([
          { id: "All", first_name: "All" },
          ...res.data.map((e) => ({
            id: e.id,
            employee: e.employee ?? e.emp_code ?? "", // ✅ ADD THIS
            first_name: e.first_name ?? "",
            last_name: e.last_name ?? "",
            designation: e.designation ?? "-",
            division: e.division ?? "-",
            state_name: e.state_name ?? "-",
          })),
        ]),
      );
  }, [selectedState, selectedDivision, divisions]);

  useEffect(() => {
    fetchEmployees();
  }, [selectedState, selectedDivision]);

  useEffect(() => {
    if (selectedState === "All" || selectedEmployee === "All") {
      setChemistList([{ id: "All", chemist_name: "All" }]);
      setSelectedChemist("All");
      return;
    }

    const params = new URLSearchParams();
    params.set("state_id", selectedState);
    params.set("employee_id", selectedEmployee); // ✅ FIX

    axios
      .get(`${BASE_URL}/api/chemist/chemists/filter?${params.toString()}`, {
        headers: getAuthHeaders(),
      })
      .then((res) =>
        setChemistList([{ id: "All", chemist_name: "All" }, ...res.data]),
      );
  }, [selectedState, selectedEmployee]);

  /** Fetch Table Data */
  const handleGoClick = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      if (selectedState && selectedState !== "All")
        params.set("state_id", selectedState);

      if (selectedDivision !== "All") {
        const d = divisions.find(
          (x) => String(x.id) === String(selectedDivision),
        );
        if (d?.name) params.set("division", d.name);
      }

      if (selectedEmployee !== "All")
        params.set("employee_id", selectedEmployee);
      if (selectedChemist !== "All") params.set("chemist_id", selectedChemist);

      params.set("start_date", startDate);
      params.set("end_date", endDate);

      const res = await axios.get(
        `${BASE_URL}/api/reports/employee-monthly-product-pob?${params.toString()}`,
        { headers: getAuthHeaders() },
      );

      setTableData(res.data?.products?.map(mapRow) || []);
      setShowContainers(true);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <h2 className="text-2xl font-bold text-center mb-3">
          <span className="text-blue-700">Monthly</span>{" "}
          <span className="text-blue-500">Product Qty</span>
        </h2>

        {/* FILTER SECTION */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm  p-3 ">
          <div className="space-y-2">
            <div className="flex items-center">
              <label className="w-24 font-medium text-gray-800">State</label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="flex-1 border border-gray-400 rounded-sm h-8 px-2"
              >
                {states.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <label className="w-24">Employee</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="border px-2 h-8 flex-1"
              >
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.id === "All"
                      ? "All"
                      : `${emp.first_name ?? ""} ${emp.last_name ?? ""} - ${emp.employee ?? ""} | ${emp.designation} | ${emp.division} | ${emp.state_name}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <label className="w-24">Start</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border px-2 h-8 w-40"
              />
            </div>
          </div>

          {/* RIGHT */}
          <div className="space-y-3">
            <div className="flex items-center">
              <label className="w-24">Division</label>
              <select
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                className="flex-1 border border-gray-400 rounded-sm h-8 px-2"
              >
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <label className="w-24">Chemist</label>
              <select
                value={selectedChemist}
                onChange={(e) => setSelectedChemist(e.target.value)}
                className="border px-2 h-8 flex-1"
              >
                <option value="All">All</option>

                {chemistList
                  .filter((c) => c?.id !== "All")
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.chemist_name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex items-center">
              <label className="w-24">End</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border px-2 h-8 w-40"
              />
            </div>
          </div>
        </div>

        {/* GO BUTTON */}
        <div className="flex justify-center mt-4">
          <CustomButton
            onClick={handleGoClick}
            isLoading={isLoading}
            variant="save"
          >
            {isLoading ? <Loader /> : "GO"}
          </CustomButton>
        </div>
      </div>

      {showContainers && (
        <div className="mt-6">
          <div className="border rounded shadow  table-wrapper">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr className="text-white font-semibold text-center bg-blue-600">
                  <th className="border px-2 py-2 sticky left-0 z-20 bg-blue-600">
                    S.No
                  </th>
                  <th className="border px-2 py-2 sticky left-[45px] z-20 bg-blue-600">
                    Code
                  </th>
                  <th className="border px-2 py-2 text-left">Product</th>
                  <th className="border px-2 py-2">Pack</th>
                  <th className="border px-2 py-2">Rate</th>
                  <th className="border px-2 py-2">Total Qty</th>
                  <th className="border px-2 py-2">Total POB</th>

                  {dateList.map((d, i) => {
                    const isSunday = d.dateObj.getDay() === 0;
                    return (
                      <th
                        key={i}
                        className={`border px-1 py-2 min-w-[55px] text-[11px] font-bold ${
                          isSunday
                            ? "bg-green-400 text-black"
                            : "bg-blue-600 text-white"
                        }`}
                      >
                        {d.date}
                        <br />
                        <span className="text-[10px]">
                          {isSunday ? "SUN" : d.dayShort}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* BODY */}
              <tbody>
                {tableData.map((p, i) => (
                  <tr
                    key={i}
                    className={`hover:bg-blue-50 ${
                      i % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    {/* Sticky Columns */}
                    <td className="border text-center sticky left-0 bg-inherit">
                      {i + 1}
                    </td>
                    <td className="border text-center sticky left-[45px] bg-inherit">
                      {p.product_id}
                    </td>

                    {/* Fixed Columns */}
                    <td className="border px-2 text-left">{p.product_name}</td>
                    <td className="border text-center">{p.packing}</td>
                    <td className="border text-center">{Math.round(p.rate)}</td>
                    <td className="border text-center font-semibold">
                      {p.total_qty}
                    </td>
                    <td className="border text-center font-semibold">
                      {Math.round(p.total_pob)}
                    </td>

                    {/* Daily QTY Only */}
                    {dateList.map((d, idx) => {
                      const qty = p?.days[d.apiKey]?.qty || 0;
                      const isSunday = d.dateObj.getDay() === 0;

                      return (
                        <td
                          key={idx}
                          className={`border text-center px-1 ${
                            isSunday ? "bg-green-100 font-semibold" : ""
                          }`}
                          style={{ minWidth: 55 }}
                        >
                          {qty}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateWiseProduct;
